import { proxy, useSnapshot } from 'valtio'
import { requestTeamList } from '../api/team-api'
import {
  flattenTeamGroups,
  normalizeTeamGroups,
  normalizeTeamItem,
  pickInitialTeamId,
  type TeamGroup,
  type TeamItem,
} from './types'
import {
  clearAiWatermarkPreference,
  syncAiWatermarkPreference,
} from './ai-watermark-preference-store'
import { resolveAiWatermarkValueFromPayload } from './ai-watermark'

type TeamStatus = 'idle' | 'loading' | 'ready' | 'error'

interface TeamState {
  status: TeamStatus
  groups: TeamGroup[]
  currentTeamId: string
  currentUserKey: string
  errorMessage: string
}

const TEAM_STORAGE_KEY_PREFIX = 'ai-canvas-client:selected-team:'

function readLocal(key: string) {
  if (typeof window === 'undefined') {
    return ''
  }

  try {
    return window.localStorage.getItem(key)?.trim() || ''
  } catch {
    return ''
  }
}

function writeLocal(key: string, value: string) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    if (value) {
      window.localStorage.setItem(key, value)
      return
    }

    window.localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

function buildStorageKey(userKey: string) {
  return `${TEAM_STORAGE_KEY_PREFIX}${userKey}`
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  return value as Record<string, unknown>
}

function findTeamById(groups: readonly TeamGroup[], teamId: string) {
  if (!teamId) {
    return null
  }

  return flattenTeamGroups(groups).find((team) => team.teamId === teamId) ?? null
}

function mergeCurrentTeamRuntimeFields(
  nextTeam: TeamItem,
  previousTeam: TeamItem | null | undefined,
) {
  if (!previousTeam || previousTeam.teamId !== nextTeam.teamId) {
    return nextTeam
  }

  return {
    ...nextTeam,
    pointLimitMonth: nextTeam.pointLimitMonth ?? previousTeam.pointLimitMonth,
    pointUsedMonth: nextTeam.pointUsedMonth ?? previousTeam.pointUsedMonth,
    pointRemainMonth: nextTeam.pointRemainMonth ?? previousTeam.pointRemainMonth,
    teamPointLimitMonth: nextTeam.teamPointLimitMonth ?? previousTeam.teamPointLimitMonth,
    pointBalancePermanent: nextTeam.pointBalancePermanent ?? previousTeam.pointBalancePermanent,
    pointBalance: nextTeam.pointBalance ?? previousTeam.pointBalance,
    aiWatermark: nextTeam.aiWatermark ?? previousTeam.aiWatermark,
  } satisfies TeamItem
}

function preserveCurrentTeamFields(groups: TeamGroup[], previousCurrentTeam: TeamItem | null | undefined) {
  if (!previousCurrentTeam) {
    return groups
  }

  return groups.map((group) => ({
    ...group,
    teams: group.teams.map((team) => mergeCurrentTeamRuntimeFields(team, previousCurrentTeam)),
  }))
}

function resetTeamState(nextUserKey = '') {
  teamStore.status = 'idle'
  teamStore.groups = []
  teamStore.currentTeamId = ''
  teamStore.currentUserKey = nextUserKey
  teamStore.errorMessage = ''

  if (!nextUserKey) {
    clearAiWatermarkPreference()
    return
  }

  syncAiWatermarkPreference({
    userKey: nextUserKey,
    teamId: '',
    aiWatermark: null,
  })
}

function syncCurrentTeamAiWatermarkPreference(
  userKey: string,
  team: TeamItem | null | undefined,
  fallbackAiWatermark?: number | null,
) {
  syncAiWatermarkPreference({
    userKey,
    teamId: team?.teamId ?? '',
    aiWatermark: team?.aiWatermark ?? fallbackAiWatermark ?? undefined,
  })
}

function ensureUserScope(userKey: string) {
  const normalizedUserKey = userKey.trim()

  if (!normalizedUserKey) {
    resetTeamState('')
    return ''
  }

  if (teamStore.currentUserKey !== normalizedUserKey) {
    resetTeamState(normalizedUserKey)
  }

  return normalizedUserKey
}

export const teamStore = proxy<TeamState>({
  status: 'idle',
  groups: [],
  currentTeamId: '',
  currentUserKey: '',
  errorMessage: '',
})

let loadPromise: Promise<void> | null = null
let loadingUserKey = ''

export function clearTeamState() {
  resetTeamState('')
}

/**
 * 使用登录时下发的当前团队数据初始化团队状态。
 * 团队选择列表统一通过 /zs/AiCanvasTeam/teamList 获取。
 */
export function initializeTeams(userKey: string, loginData: { current_team?: any; user_info?: any }) {
  const normalizedUserKey = ensureUserScope(userKey)

  if (!normalizedUserKey) {
    return
  }

  const { current_team: currentTeam } = loginData

  if (!currentTeam) {
    return
  }
  const nextStatus = teamStore.status === 'ready' ? 'ready' : 'idle'
  const groups = teamStore.groups.map((group) => ({
    ...group,
    teams: [...group.teams],
  }))
  const currentTeamId = String(currentTeam.team_id || '').trim()

  if (!currentTeamId) {
    return
  }

  const currentTeamRecord = asRecord(currentTeam)
  const mergedCurrentTeamPayload = {
    ...(currentTeamRecord ?? {}),
  }

  const enrichedItem = normalizeTeamItem(mergedCurrentTeamPayload)
  if (!enrichedItem) {
    return
  }

  let found = false
  for (const group of groups) {
    const idx = group.teams.findIndex((team) => team.teamId === currentTeamId)
    if (idx === -1) {
      continue
    }

    ;(group.teams as TeamItem[])[idx] = enrichedItem
    found = true
  }

  if (!found) {
    if (groups.length === 0) {
      groups.push({
        key: enrichedItem.isPersonalTeam ? 'personal' : 'all',
        label: enrichedItem.isPersonalTeam ? '个人账号' : '团队列表',
        teams: [enrichedItem],
      })
    } else {
      ;(groups[0].teams as TeamItem[]) = [enrichedItem, ...groups[0].teams]
    }
  }

  teamStore.groups = groups
  teamStore.currentTeamId = currentTeamId
  teamStore.status = nextStatus
  teamStore.errorMessage = ''

  syncCurrentTeamAiWatermarkPreference(
    normalizedUserKey,
    enrichedItem,
    resolveAiWatermarkValueFromPayload(loginData.user_info),
  )

  writeLocal(buildStorageKey(normalizedUserKey), currentTeamId)
}

export async function loadTeamList(userKey: string, force = false) {
  const normalizedUserKey = ensureUserScope(userKey)

  if (!normalizedUserKey) {
    return
  }

  if (!force && teamStore.status === 'ready') {
    return
  }

  if (loadPromise && loadingUserKey === normalizedUserKey) {
    return loadPromise
  }

  teamStore.status = 'loading'
  teamStore.errorMessage = ''
  loadingUserKey = normalizedUserKey

  loadPromise = (async () => {
    const response = await requestTeamList()

    if (teamStore.currentUserKey !== normalizedUserKey) {
      return
    }

    if (response.code !== 1) {
      teamStore.status = 'error'
      teamStore.errorMessage = response.msg || '加载团队列表失败'
      return
    }

    const previousCurrentTeam = findTeamById(teamStore.groups, teamStore.currentTeamId)
    const groups = preserveCurrentTeamFields(normalizeTeamGroups(response.data), previousCurrentTeam)
    const storedTeamId = readLocal(buildStorageKey(normalizedUserKey))
    const nextTeamId = pickInitialTeamId(groups, storedTeamId)

    teamStore.groups = groups
    teamStore.currentTeamId = nextTeamId
    teamStore.status = 'ready'
    teamStore.errorMessage = ''

    syncCurrentTeamAiWatermarkPreference(
      normalizedUserKey,
      findTeamById(groups, nextTeamId),
    )

    writeLocal(buildStorageKey(normalizedUserKey), nextTeamId)
  })()
    .catch(() => {
      if (teamStore.currentUserKey !== normalizedUserKey) {
        return
      }

      teamStore.status = 'error'
      teamStore.errorMessage = '加载团队列表失败'
    })
    .finally(() => {
      if (loadingUserKey === normalizedUserKey) {
        loadPromise = null
        loadingUserKey = ''
      }
    })

  return loadPromise
}

export function selectTeam(teamId: string, userKey: string) {
  const normalizedUserKey = ensureUserScope(userKey)
  const nextTeam = findTeamById(teamStore.groups, teamId)

  if (!normalizedUserKey || !nextTeam) {
    return null
  }

  teamStore.currentTeamId = nextTeam.teamId
  writeLocal(buildStorageKey(normalizedUserKey), nextTeam.teamId)
  syncCurrentTeamAiWatermarkPreference(normalizedUserKey, nextTeam)

  return nextTeam
}

export function updateCurrentTeamAiWatermark(aiWatermark: number) {
  const currentTeam = findTeamById(teamStore.groups, teamStore.currentTeamId)

  if (!currentTeam) {
    syncAiWatermarkPreference({
      userKey: teamStore.currentUserKey,
      teamId: teamStore.currentTeamId,
      aiWatermark,
    })
    return null
  }

  const nextGroups = teamStore.groups.map((group) => ({
    ...group,
    teams: group.teams.map((team) => (
      team.teamId === currentTeam.teamId
        ? { ...team, aiWatermark }
        : team
    )),
  }))

  teamStore.groups = nextGroups
  syncCurrentTeamAiWatermarkPreference(
    teamStore.currentUserKey,
    findTeamById(nextGroups, currentTeam.teamId),
    aiWatermark,
  )

  return findTeamById(nextGroups, currentTeam.teamId)
}

export function useTeamState() {
  const snapshot = useSnapshot(teamStore)
  const teams = flattenTeamGroups(snapshot.groups)
  const currentTeam = teams.find((team) => team.teamId === snapshot.currentTeamId) ?? null

  return {
    currentTeam,
    currentTeamId: snapshot.currentTeamId,
    errorMessage: snapshot.errorMessage,
    groups: snapshot.groups,
    hasTeams: teams.length > 0,
    status: snapshot.status,
  }
}

export function getCurrentTeam(): TeamItem | null {
  return findTeamById(teamStore.groups, teamStore.currentTeamId)
}
